import { tr } from '@/lib/i18n-core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarX2 } from 'lucide-react';
import { get, put } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { useWorkingBranchId } from '@/lib/branch';
import { display, todayIso } from '@/lib/utils';
import { EmptyState } from '@/components/ui/layout';
import { Alert, Button, Spinner } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { AppointmentSheet } from './AppointmentHoverCard';
import { CalendarGrid, PX_PER_HOUR, PX_PER_HOUR_COMPACT, type SlotTarget } from './CalendarGrid';
import { CalendarToolbar } from './CalendarToolbar';
import { restoredDepartment, useCalendarPrefs } from './calendar-prefs';
import { slotVerdict } from './calendar-slot';
import { isoAtMinute, providerSpans, rangeFor, rowPxPerHour, snapToSessions, type SessionSpan } from './calendar-time';
import type { CalendarAppointment, CalendarColumn, CalendarProvider, CalendarSnapshot, CalendarView } from './calendar-types';
import { selectionScope, useProviderSelection, visibleProviders } from './provider-selection';
import { useCalendarDrag, type DragTarget } from './useCalendarDrag';
import { useCalendarSnapshot, useNowMinute } from './useCalendarSnapshot';
import { usePhoneLayout } from './useMediaQuery';

const SETTLE_MS = 120;
const SWIPE_PX = 60;

interface Department {
  id: string;
  name: string;
  branchId: string;
}

function dayLabel(date: string): string {
  return new Intl.DateTimeFormat(display.locale, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${date}T00:00:00Z`));
}

function buildColumns(snapshot: CalendarSnapshot, providers: CalendarProvider[], view: CalendarView): CalendarColumn[] {
  if (view === 'week') {
    return snapshot.days.map((day, dayIndex) => ({
      key: `day:${day.date}`,
      dayIndex,
      providerIds: providers.map((provider) => provider.id),
      label: dayLabel(day.date),
      sublabel: null,
    }));
  }
  return providers.map((provider) => ({
    key: `provider:${provider.id}`,
    dayIndex: 0,
    providerIds: [provider.id],
    label: provider.name,
    sublabel: provider.designation ?? provider.specialty,
  }));
}

function moveAppointment(snapshot: CalendarSnapshot, id: string, startsAt: string, endsAt: string, providerId: string): CalendarSnapshot {
  return {
    ...snapshot,
    appointments: snapshot.appointments.map((appointment) =>
      appointment.id === id ? { ...appointment, startsAt, endsAt, providerId } : appointment,
    ),
  };
}

export function AppointmentCalendarTab() {
  const { user, can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const phone = usePhoneLayout();
  const canManage = can('appointment.manage');

  const branches = user?.branches ?? [];
  const [branchId, setBranchId] = useState(useWorkingBranchId());
  const prefs = useCalendarPrefs();
  const [departmentId, setDepartmentId] = useState(() => restoredDepartment(prefs.departments, branchId, user?.departmentId));
  const [date, setDate] = useState(todayIso());
  const { view, setView, compact, setCompact } = prefs;
  const [activeId, setActiveId] = useState<string | null>(null);
  const [pinned, setPinned] = useState(false);
  const [settlingId, setSettlingId] = useState<string | null>(null);
  const [phoneProvider, setPhoneProvider] = useState(0);

  const departments = useQuery({
    queryKey: ['departments', branchId],
    queryFn: () => get<Department[]>(`/org/departments?branchId=${branchId}`),
    enabled: Boolean(branchId) && can('department.read'),
    staleTime: 300_000,
  });

  useEffect(() => {
    const known = departments.data;
    if (known && departmentId && !known.some((department) => department.id === departmentId)) setDepartmentId('');
  }, [departments.data, departmentId]);

  const { from, days } = rangeFor(date, view);
  const effectiveView: CalendarView = phone ? 'day' : view;
  const range = phone ? rangeFor(date, 'day') : { from, days };
  const { query, connected, key } = useCalendarSnapshot(branchId, departmentId, range.from, range.days);
  const snapshot = query.data;
  const now = useNowMinute(snapshot?.days);

  const scope = selectionScope(branchId, departmentId);
  const chosen = useProviderSelection((state) => state.selections[scope]);
  const select = useProviderSelection((state) => state.select);
  const reset = useProviderSelection((state) => state.reset);
  const allProviders = useMemo(() => snapshot?.providers ?? [], [snapshot?.providers]);
  const selectedProviders = useMemo(() => visibleProviders(allProviders, chosen), [allProviders, chosen]);
  const phoneIndex = Math.min(phoneProvider, Math.max(selectedProviders.length - 1, 0));
  const shownProviders = phone ? selectedProviders.slice(phoneIndex, phoneIndex + 1) : selectedProviders;
  const columns = useMemo(() => (snapshot ? buildColumns(snapshot, shownProviders, effectiveView) : []), [snapshot, shownProviders, effectiveView]);
  const spans = useMemo(() => {
    const byColumn = new Map<string, Map<string, SessionSpan[]>>();
    if (!snapshot) return byColumn;
    for (const column of columns) {
      const day = snapshot.days[column.dayIndex];
      if (!day) continue;
      const byProvider = new Map<string, SessionSpan[]>();
      for (const provider of shownProviders) {
        if (column.providerIds.includes(provider.id)) byProvider.set(provider.id, providerSpans(day, provider.sessions));
      }
      byColumn.set(column.key, byProvider);
    }
    return byColumn;
  }, [snapshot, columns, shownProviders]);
  const pxPerHour = useMemo(() => {
    const lengths: number[] = [];
    for (const byProvider of spans.values()) for (const list of byProvider.values()) for (const span of list) lengths.push(span.slotMinutes);
    return rowPxPerHour(compact ? PX_PER_HOUR_COMPACT : PX_PER_HOUR, lengths);
  }, [spans, compact]);
  const snapInColumn = useCallback(
    (columnKey: string, providerId: string, minute: number) => snapToSessions(minute, spans.get(columnKey)?.get(providerId) ?? []),
    [spans],
  );

  const columnElements = useRef(new Map<string, HTMLElement>());
  const registerColumn = useCallback((columnKey: string, element: HTMLElement | null) => {
    if (element) columnElements.current.set(columnKey, element);
    else columnElements.current.delete(columnKey);
  }, []);

  const onActivate = useCallback((id: string | null, pin: boolean) => {
    setActiveId(id);
    setPinned(id ? pin : false);
  }, []);

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['calendar'] });
    void queryClient.invalidateQueries({ queryKey: ['appointments'] });
    void queryClient.invalidateQueries({ queryKey: ['queue'] });
  }, [queryClient]);

  const onDrop = useCallback(
    async (appointment: CalendarAppointment, target: DragTarget) => {
      await queryClient.cancelQueries({ queryKey: key });
      const current = queryClient.getQueryData<CalendarSnapshot>(key);
      const day = current?.days[target.dayIndex];
      if (!current || !day) return;
      const length = Math.max(appointment.durationMinutes, 10);
      const slotAt = isoAtMinute(day, target.startMinute);
      const endsAt = isoAtMinute(day, target.startMinute + length);
      queryClient.setQueryData<CalendarSnapshot>(key, moveAppointment(current, appointment.id, slotAt, endsAt, target.providerId));
      setSettlingId(appointment.id);
      setTimeout(() => setSettlingId((id) => (id === appointment.id ? null : id)), SETTLE_MS);
      try {
        await put(`/appointments/${appointment.id}/reschedule`, {
          slotAt,
          ...(target.providerId !== appointment.providerId ? { doctorId: target.providerId } : {}),
          ...(appointment.durationMinutes > 0 ? { durationMinutes: appointment.durationMinutes } : {}),
          notifyPatient: true,
        });
        toast.success(tr('opd', 'appointment_moved'));
        refresh();
      } catch (error) {
        queryClient.setQueryData<CalendarSnapshot>(key, (latest) =>
          latest ? moveAppointment(latest, appointment.id, appointment.startsAt, appointment.endsAt, appointment.providerId) : latest,
        );
        toast.error(tr('opd', 'could_not_move_the_appointment'), describeError(error));
      }
    },
    [key, queryClient, refresh, toast],
  );

  const drag = useCalendarDrag({
    hours: snapshot?.hours ?? { fromMinute: 0, toMinute: 1440 },
    pxPerHour,
    columns,
    columnElement: (columnKey) => columnElements.current.get(columnKey) ?? null,
    onDrop: (appointment, target) => void onDrop(appointment, target),
    enabled: canManage,
    snap: snapInColumn,
  });

  useEffect(() => {
    if (drag.drag) setActiveId(null);
  }, [drag.drag]);

  const onSlotClick = useCallback(
    ({ column, minute, providerId }: SlotTarget) => {
      const day = snapshot?.days[column.dayIndex];
      if (!snapshot || !day || column.providerIds.length === 0) return;
      const slotAt = isoAtMinute(day, minute);
      const candidates = providerId && column.providerIds.includes(providerId) ? [providerId] : column.providerIds;
      const verdict = slotVerdict(snapshot, candidates, column.dayIndex, slotAt, Date.now());
      if (verdict.kind === 'outside_session') {
        toast.warning(tr('opd', 'slot_outside_session'), tr('opd', 'slot_outside_session_hint'));
        return;
      }
      if (verdict.kind === 'blocked') {
        toast.warning(tr('opd', 'slot_blocked'), tr('opd', 'slot_blocked_hint'));
        return;
      }
      if (verdict.kind === 'past') {
        toast.warning(tr('opd', 'slot_past'), tr('opd', 'slot_past_hint'));
        return;
      }
      setActiveId(null);
      const search = new URLSearchParams({ doctorId: verdict.providerId, date: day.date, slotAt, returnTo: '/appointments' });
      if (departmentId) search.set('departmentId', departmentId);
      navigate(`/appointments/new?${search}`);
    },
    [snapshot, toast, navigate, departmentId],
  );

  const swipeStart = useRef<number | null>(null);
  const onTouchStart = (clientX: number) => {
    swipeStart.current = clientX;
  };
  const onTouchEnd = (clientX: number) => {
    if (swipeStart.current === null || !phone) return;
    const delta = clientX - swipeStart.current;
    swipeStart.current = null;
    if (Math.abs(delta) < SWIPE_PX || drag.drag) return;
    const direction = document.documentElement.dir === 'rtl' ? -1 : 1;
    const next = delta * direction < 0 ? phoneIndex + 1 : phoneIndex - 1;
    setPhoneProvider(Math.min(Math.max(next, 0), Math.max(selectedProviders.length - 1, 0)));
  };

  const activeAppointment = activeId ? snapshot?.appointments.find((appointment) => appointment.id === activeId) : undefined;
  const activeProvider = activeAppointment ? allProviders.find((provider) => provider.id === activeAppointment.providerId) : undefined;

  if (!branchId) return <Alert variant="warning">{tr('opd', 'your_account_has_no_branch_ask_an_administrator')}</Alert>;

  return (
    <div>
      <CalendarToolbar
        branches={branches}
        branchId={branchId}
        onBranch={(id) => {
          setBranchId(id);
          setDepartmentId(restoredDepartment(prefs.departments, id, null));
        }}
        departments={departments.data}
        departmentId={departmentId}
        onDepartment={(id) => {
          setDepartmentId(id);
          prefs.setDepartment(branchId, id);
        }}
        date={date}
        onDate={setDate}
        view={view}
        onView={setView}
        providers={allProviders}
        selected={selectedProviders.map((provider) => provider.id)}
        onProviders={(ids) => select(scope, ids)}
        onResetProviders={() => reset(scope)}
        compact={compact}
        onCompact={setCompact}
        connected={connected}
        phone={phone}
      />

      {query.isLoading && <Spinner />}
      {query.error && <Alert variant="destructive">{describeError(query.error)}</Alert>}

      {snapshot && snapshot.truncated && <Alert variant="warning" className="mb-3">{tr('opd', 'calendar_truncated')}</Alert>}

      {snapshot && allProviders.length === 0 && (
        <EmptyState
          icon={CalendarX2}
          title={tr('opd', 'no_providers_to_show')}
          description={tr('opd', 'no_providers_to_show_hint')}
          action={
            <Button variant="outline" onClick={() => navigate('/schedules')}>
              {tr('opd', 'open_clinic_hours')}
            </Button>
          }
        />
      )}

      {snapshot && allProviders.length > 0 && (
        <div
          onTouchStart={(event) => onTouchStart(event.touches[0]?.clientX ?? 0)}
          onTouchEnd={(event) => onTouchEnd(event.changedTouches[0]?.clientX ?? 0)}
        >
          {phone && selectedProviders.length > 1 && (
            <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
              <Button variant="ghost" size="sm" disabled={phoneIndex === 0} onClick={() => setPhoneProvider(phoneIndex - 1)}>
                {tr('opd', 'previous')}
              </Button>
              <span>
                {phoneIndex + 1} / {selectedProviders.length}
              </span>
              <Button variant="ghost" size="sm" disabled={phoneIndex >= selectedProviders.length - 1} onClick={() => setPhoneProvider(phoneIndex + 1)}>
                {tr('opd', 'next')}
              </Button>
            </div>
          )}
          <CalendarGrid
            snapshot={snapshot}
            columns={columns}
            pxPerHour={pxPerHour}
            now={now}
            phone={phone}
            activeId={activeId}
            pinned={pinned}
            settlingId={settlingId}
            drag={drag.drag}
            canManage={canManage}
            onActivate={onActivate}
            onSlotClick={onSlotClick}
            onChanged={refresh}
            registerColumn={registerColumn}
            dragHandlers={drag}
          />
        </div>
      )}

      {phone && activeAppointment && activeProvider && (
        <AppointmentSheet appointment={activeAppointment} provider={activeProvider} onClose={() => onActivate(null, false)} onChanged={refresh} />
      )}

    </div>
  );
}
